<template>
  <div class="_container">
    <div class="_header">
      <div class="header_title" v-text="$t('utils.docx_templates')" />
      <BaseButton
        icon="$mdiFileCog"
        type="primary"
        @click="importDialogOpen = true"
      >
        {{ $t("utils.import") }}
      </BaseButton>
    </div>
    <BaseDataTable
      :items="docxTemplates.items"
      :loading="loading"
      :options="{
        page: filters.page,
        itemsPerPage: filters.page_size,
      }"
      :server-items-length="docxTemplates.count"
      :loading-text="$t('utils.loadingData')"
      @update:options="onPaginationChange"
    >
      <template #header>
        <thead>
          <tr>
            <th>
              {{ $tc("utils.name", 1) }}
            </th>
            <th class="header-col text-center">{{ $t("utils.used") }}</th>
            <th class="text-end">
              {{ $tc("utils.action", 1) }}
            </th>
          </tr>
        </thead>
      </template>
      <template #item="{ item }">
        <tr class="tab_row">
          <td>
            <div class="row_item">
              <a
                class="item_label"
                target="_blank"
                :href="item.file"
                :download="item.name"
                v-text="item.name"
              />
            </div>
          </td>
          <td>
            <div class="usage_item">
              <div
                class="usage_icon"
                :class="{
                  danger: !item.isUsed,
                }"
                @click="displayUsage(item)"
              >
                <span v-if="item.isUsed" v-text="item.usageCount" />
                <BaseIcon v-else icon="$close" color="white" small />
              </div>
            </div>
          </td>
          <td>
            <div class="action_section">
              <BaseButtonIcon
                small
                color="primary"
                icon="$mdiPencilOutline"
                @click="editDocxTemplate(item)"
              />
              <BaseButtonIcon
                small
                color="secondary"
                icon="$mdiTrashCanOutline"
                @click="openRemoveDocxDialog(item)"
              />
            </div>
          </td>
        </tr>
      </template>
    </BaseDataTable>
    <DocxTemplateImportDialog
      v-if="importDialogOpen"
      :organization-id="organizationId"
      :docx-template="selectedDocx"
      @close="onCloseImportDocxDialog"
      @fetch-docx-templates="fetchFetchDocxTemplate"
      @docx-templates-edited="onDocxTemplateEdited"
    />
    <DocxTemplateRemoveDialog
      v-if="removeDialogOpen"
      :organization-id="organizationId"
      :docx-template="selectedDocx"
      @close="onCloseRemoveDocxDialog"
      @docx-removed="onDocxTemplateRemoved"
    />
    <DocxTemplateUsageDialog
      :dialog-state="usageDialogOpen"
      :docx-template="selectedDocx"
      :organization-id="organizationId"
      @close="usageDialogOpen = false"
    />
  </div>
</template>

<script>
import { DocxTemplate } from "@/core/Models/DocxTemplate";
import OrganizationAPI from "@/core/Services/organization.api";
import DocxTemplateImportDialog from "@/modules/Admin/Components/DocxTemplateImportDialog.vue";
import DocxTemplateRemoveDialog from "@/modules/Admin/Components/DocxTemplateRemoveDialog.vue";
import DocxTemplateUsageDialog from "@/modules/Admin/Components/DocxTemplateUsageDialog.vue";
export default {
  name: "DocxTemplate",
  components: {
    DocxTemplateImportDialog,
    DocxTemplateRemoveDialog,
    DocxTemplateUsageDialog,
  },
  props: {
    organizationId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      importDialogOpen: false,
      removeDialogOpen: false,
      usageDialogOpen: false,
      selectedDocx: null,
      docxTemplates: {
        items: [],
        count: 0,
      },
      loading: true,
      filters: {
        page_size: 10,
        page: 1,
      },
    };
  },
  methods: {
    async fetchFetchDocxTemplate() {
      this.loading = true;
      try {
        const response = await OrganizationAPI.getOrganizationDocxTemplate({
          organizationId: this.organizationId,
          filters: this.filters,
        });
        this.docxTemplates = {
          ...response.data,
          items: response.data.results.map((d) => new DocxTemplate(d)),
        };
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.loading = false;
    },
    onPaginationChange(pagination) {
      this.filters.page = pagination.page;
      this.filters.page_size =
        pagination.itemsPerPage < 0
          ? this.docxTemplates.count
          : pagination.itemsPerPage;
      this.fetchFetchDocxTemplate();
    },
    editDocxTemplate(docxTemplate) {
      this.selectedDocx = docxTemplate;
      this.importDialogOpen = true;
    },
    onDocxTemplateEdited(docxTemplate) {
      this.onCloseImportDocxDialog();
      const tmp = this.docxTemplates.items.find((e) => e.id == docxTemplate.id);
      if (!tmp) return;
      tmp.name = docxTemplate.name;
      tmp.file = docxTemplate.file;
    },
    onCloseImportDocxDialog() {
      this.importDialogOpen = false;
      this.selectedDocx = null;
    },
    openRemoveDocxDialog(docxTemplate) {
      this.selectedDocx = docxTemplate;
      this.removeDialogOpen = true;
    },
    onDocxTemplateRemoved(docxTemplate) {
      this.onCloseRemoveDocxDialog();
      this.docxTemplates.items = this.docxTemplates.items.filter(
        (e) => e.id != docxTemplate.id
      );
    },
    onCloseRemoveDocxDialog() {
      this.removeDialogOpen = false;
      this.selectedDocx = null;
    },
    displayUsage(item) {
      if (!item.isUsed) return;
      this.selectedDocx = item;
      this.usageDialogOpen = true;
    },
  },
};
</script>

<style scoped lang="scss">
._container {
  ._header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
    gap: 16px;
    .header_title {
      font-weight: 600;
      font-size: 24px;
      line-height: 30px;
      color: #353542;
      margin-top: 8px;
      margin-right: auto;
    }
  }
  .tab_row {
    .row_item {
      padding: 16px 0;
      font-family: "Inter";
      font-style: normal;
      .item_label {
        color: #2536cc;
        font-size: 12px;
        font-weight: 600;
        line-height: 20px;
        cursor: pointer;
      }
    }
    .action_section {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 4px;
    }
  }
  .usage_item {
    display: flex;
    justify-content: center;
    .usage_icon {
      cursor: pointer;
      width: 20px;
      height: 20px;
      font-family: "Inter";
      font-size: 13px;
      line-height: 13px;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #18a300;
      border-radius: 50%;
      &.danger {
        cursor: auto;
        background: #ff5267;
      }
    }
  }
}
</style>
